import { getPageConfig, isSortAvailable, getCustomFilterConfigs, getFilterVisibility } from '@/data/config'

export type ViewMode = 'grid' | 'split' | 'map'
export type CardType = 'units' | 'plans' | 'properties'

/**
 * The full set of list-screen state that can be encoded into the URL hash so a
 * filtered/sorted view can be shared or restored ("Quick Search").
 */
export interface ListUrlState {
	tab: CardType
	view: ViewMode
	sort: string
	beds: string[]
	priceMin?: number
	priceMax?: number
	moveIn: string
	page: number
	customFilters: Record<string, string[]>
	/** Portfolio only: Engrain asset ID restricting the unit list to one property. */
	property?: string
}

/** Defaults used to omit redundant params from the serialized hash. */
export interface ListUrlDefaults {
	tab: CardType
	view: ViewMode
	sort: string
}

/** Derive the set of valid bedroom filter values from config (mirrors App.bedOptions). */
function validBedValues (): Set<string> {
	return new Set(
		getPageConfig().filters.bedrooms.map(b =>
			b.cssClasses ? `custom:${b.cssClasses.join('.')}` : String(b.value)
		)
	)
}

/** Parse a non-negative integer, returning undefined for anything else. */
function parseNonNegativeInt (raw: string | null): number | undefined {
	if (raw == null || !/^\d+$/.test(raw)) return undefined
	const n = parseInt(raw, 10)
	return Number.isFinite(n) ? n : undefined
}

/** True when the value is a real YYYY-MM-DD calendar date. */
function isValidIsoDate (value: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
	const d = new Date(`${value}T00:00:00`)
	return !isNaN(d.getTime())
}

/**
 * Serialize list state into a URL query string (no leading `?`), omitting any
 * value that matches its default so shared links stay clean.
 */
export function serializeListState (state: ListUrlState, defaults: ListUrlDefaults): string {
	const params: string[] = []
	const enc = encodeURIComponent

	if (state.tab !== defaults.tab) params.push(`tab=${state.tab}`)
	if (state.view !== defaults.view) params.push(`view=${state.view}`)
	if (state.sort !== defaults.sort) params.push(`sort=${enc(state.sort)}`)
	if (state.beds.length > 0) params.push(`beds=${state.beds.map(enc).join(',')}`)
	if (state.priceMin != null) params.push(`price_min=${state.priceMin}`)
	if (state.priceMax != null) params.push(`price_max=${state.priceMax}`)
	if (state.moveIn) params.push(`move_in=${state.moveIn}`)

	for (const [slug, values] of Object.entries(state.customFilters)) {
		if (values.length > 0) params.push(`cf_${enc(slug)}=${values.map(enc).join(',')}`)
	}

	if (state.page > 1) params.push(`page=${state.page}`)
	if (state.property) params.push(`property=${enc(state.property)}`)

	return params.join('&')
}

/**
 * Parse a URL query string into a partial list state, validating every value
 * against the current config. Unknown or invalid params are silently dropped so
 * a malformed/quick-search link can never put the app into an invalid state.
 */
export function parseListState (query: string): Partial<ListUrlState> {
	const sp = new URLSearchParams(query)
	const result: Partial<ListUrlState> = {}
	const pageConfig = getPageConfig()
	const portfolio = pageConfig.mode === 'portfolio'

	const tab = sp.get('tab')
	if (tab === 'units' || (tab === 'plans' && !portfolio) || (tab === 'properties' && portfolio)) result.tab = tab

	const view = sp.get('view')
	if (view === 'grid' || view === 'split' || view === 'map') result.view = view

	const sort = sp.get('sort')
	if (sort && isSortAvailable(sort)) result.sort = sort

	const beds = sp.get('beds')
	if (beds) {
		const valid = validBedValues()
		const list = beds.split(',').map(b => b.trim()).filter(b => valid.has(b))
		if (list.length > 0) result.beds = list
	}

	Object.assign(result, parseCoreFilterParams(sp))

	const customFilters = parseCustomFilterParams(sp)
	if (Object.keys(customFilters).length > 0) result.customFilters = customFilters

	const page = parseNonNegativeInt(sp.get('page'))
	if (page != null && page >= 1) result.page = page

	const property = sp.get('property')
	if (portfolio && property && pageConfig.portfolio?.properties.some(p => p.engrainAssetId === property)) {
		result.property = property
	}

	return result
}

/** Price and move-in params, ignored when the CMS hides that filter. */
function parseCoreFilterParams (sp: URLSearchParams): Pick<Partial<ListUrlState>, 'priceMin' | 'priceMax' | 'moveIn'> {
	const result: Pick<Partial<ListUrlState>, 'priceMin' | 'priceMax' | 'moveIn'> = {}
	const visible = getFilterVisibility()

	if (visible.price) {
		const priceMin = parseNonNegativeInt(sp.get('price_min'))
		if (priceMin != null) result.priceMin = priceMin

		const priceMax = parseNonNegativeInt(sp.get('price_max'))
		if (priceMax != null) result.priceMax = priceMax
	}

	const moveIn = sp.get('move_in')
	if (visible.date && moveIn && isValidIsoDate(moveIn)) result.moveIn = moveIn

	return result
}

/** `cf_{slug}` params for configured custom filters, keeping only known option values. */
function parseCustomFilterParams (sp: URLSearchParams): Record<string, string[]> {
	const customFilters: Record<string, string[]> = {}
	for (const cf of getCustomFilterConfigs()) {
		const raw = sp.get(`cf_${cf.slug}`)
		if (!raw) continue
		const validValues = new Set(cf.options.map(o => o.value))
		const list = raw.split(',').map(v => v.trim()).filter(v => validValues.has(v))
		if (list.length > 0) customFilters[cf.slug] = list
	}
	return customFilters
}
